import React, { useState, useEffect } from 'react';
import {
  FaArrowDown,
  FaArrowUp,
  FaChevronRight,
  FaChevronDown
} from 'react-icons/fa';
import './TabelaDrilldown.css';

const CUSTO_POR_DIVISOR_BY_TOTAL = 'DataBlendingVisao.CustoPorDivisorByTotal';

const TabelaDrilldown = ({ dados, onExpand }) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [dadosHierarquicos, setDadosHierarquicos] = useState([]);
  const [objetosUnicos, setObjetosUnicos] = useState([]);

  useEffect(() => {
    if (dados && dados.length > 0) {
      const dadosHierarquicos = agruparPorCentroCustoComHierarquia(dados);
      setDadosHierarquicos(dadosHierarquicos);

      const objetosMapeados = dados
        .map(d => d['ObjetoCusto.descricao'])
        .filter(Boolean);
      const objetosUnicos = Array.from(new Set(objetosMapeados)).map(desc => ({
        descricao: desc
      }));
      setObjetosUnicos(objetosUnicos);

      setExpandedRows({});
    }
  }, [dados]);

  const handleCollapseAll = () => {
    setExpandedRows({});
  };

  const [isExpandingAll, setIsExpandingAll] = useState(false);

  const agruparPorCentroCustoComHierarquia = agruparPorCentroCustoComHierarquiaFunction();

  const handleExpandAll = handleExpandAllFunction(
    setIsExpandingAll,
    expandedRows,
    onExpand,
    agruparPorCentroCustoComHierarquia,
    setDadosHierarquicos,
    dadosHierarquicos,
    setExpandedRows
  );

  function encontrarItemPorIdRecursivo(items, id) {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children && item.children.length > 0) {
        const encontrado = encontrarItemPorIdRecursivo(item.children, id);
        if (encontrado) return encontrado;
      }
    }
    return null;
  }

  const encontrarItemPorId = encontrarItemPorIdFunction(
    encontrarItemPorIdRecursivo
  );

  const handleExpand = handleExpandFunction(
    expandedRows,
    setExpandedRows,
    encontrarItemPorId,
    dadosHierarquicos,
    onExpand,
    agruparPorCentroCustoComHierarquia,
    setDadosHierarquicos
  );

  const formatarValor = formatarValorFunction();

  const renderRow = (row, depth = 0) => {
    const rowId = row.id;
    const isExpanded = expandedRows[rowId];
    const hasChildren = row.hasChilds === true || row.hasChilds === 'true';
    const bgClass = depth % 2 === 0 ? 'bg-even' : 'bg-odd';

    return (
      <React.Fragment key={rowId}>
        <tr className={bgClass}>
          <td
            className="tabela-cell"
            style={{ paddingLeft: `${depth * 20 + 10}px` }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {hasChildren ? (
                <span
                  onClick={() => handleExpand(rowId)}
                  className="tabela-expand-icon tabela-expansivel"
                >
                  {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                </span>
              ) : (
                <span className="tabela-expand-icon tabela-nao-expansivel">
                  •
                </span>
              )}
              <span>{row.descricao}</span>
            </div>
          </td>

          {objetosUnicos.map((obj, idx) => (
            <td key={idx} className="tabela-cell">
              {formatarValor(row.objetos[obj.descricao])}
            </td>
          ))}
        </tr>

        {isExpanded &&
          row.children &&
          row.children.map(child => renderRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="tabela-container">
      <div className="tabela-header">
        <strong>Dados da Visão</strong>
        <div className="tabela-actions">
          <button
            onClick={handleExpandAll}
            className="tabela-botao"
            title="Expandir tudo"
            disabled={isExpandingAll}
          >
            <FaArrowDown color="#fff" />
          </button>

          <button
            onClick={handleCollapseAll}
            title="Recolher tudo"
            className="tabela-botao"
            disabled={isExpandingAll}
          >
            <FaArrowUp color="#fff" />
          </button>
        </div>
      </div>

      <table className="tabela">
        <thead>
          <tr>
            <th>Centro de Custo</th>
            {objetosUnicos.map((obj, idx) => (
              <th key={idx}>{obj.descricao}</th>
            ))}
          </tr>
        </thead>
        <tbody>{dadosHierarquicos.map(row => renderRow(row, 0))}</tbody>
      </table>
    </div>
  );
};

export {
  agruparPorCentroCustoComHierarquiaFunction,
  encontrarItemPorIdFunction,
  formatarValorFunction,
  handleExpandAllFunction,
  handleExpandFunction
};
export default TabelaDrilldown;

function handleExpandAllFunction(
  setIsExpandingAll,
  expandedRows,
  onExpand,
  agruparPorCentroCustoComHierarquia,
  setDadosHierarquicos,
  dadosHierarquicos,
  setExpandedRows
) {
  return async () => {
    setIsExpandingAll(true);
    const novosExpandidos = { ...expandedRows };

    const expandRecursivamente = async items => {
      for (const item of items) {
        if (item.hasChilds && !novosExpandidos[item.id]) {
          novosExpandidos[item.id] = true;

          if (!item.children || item.children.length === 0) {
            try {
              const filhos = await onExpand(item.id);
              const filhosProcessados = agruparPorCentroCustoComHierarquia(
                filhos
              );
              item.children = filhosProcessados;

              setDadosHierarquicos([...dadosHierarquicos]);
              setExpandedRows({ ...novosExpandidos });
            } catch (error) {
              console.error(`Erro ao expandir item ${item.id}:`, error);
            }
          }

          if (item.children && item.children.length > 0) {
            await expandRecursivamente(item.children);
          }
        }
      }
    };

    await expandRecursivamente(dadosHierarquicos);
    setIsExpandingAll(false);
  };
}

function handleExpandFunction(
  expandedRows,
  setExpandedRows,
  encontrarItemPorId,
  dadosHierarquicos,
  onExpand,
  agruparPorCentroCustoComHierarquia,
  setDadosHierarquicos
) {
  return async id => {
    const isExpanded = expandedRows[id];

    if (isExpanded) {
      setExpandedRows({ ...expandedRows, [id]: false });
    } else {
      const item = encontrarItemPorId(dadosHierarquicos, id);

      if (
        item &&
        item.hasChilds &&
        (!item.children || item.children.length === 0)
      ) {
        try {
          const filhos = await onExpand(id);
          const filhosAgrupados = agruparPorCentroCustoComHierarquia(filhos);
          const todosFilhos = new Map();

          const preencherMapa = itens => {
            for (const filho of itens) {
              todosFilhos.set(filho.id, filho);
              if (filho.children && filho.children.length > 0) {
                preencherMapa(filho.children);
              }
            }
          };

          preencherMapa(filhosAgrupados);
          item.children = filhosAgrupados.map(f => todosFilhos.get(f.id));

          setDadosHierarquicos([...dadosHierarquicos]);
        } catch (error) {
          console.error('Erro ao carregar filhos:', error);
        }
      }

      setExpandedRows({ ...expandedRows, [id]: true });
    }
  };
}

function agruparPorCentroCustoComHierarquiaFunction() {
  return dadosPlanos => {
    const mapa = new Map();
    const raizes = [];

    dadosPlanos.forEach(item => {
      const id = item['CentroCustoDrilldown.id'];
      const idSuperior = item['CentroCustoDrilldown.idSuperior'];
      const nomeCentro = item['CentroCustoDrilldown.descricao'];
      const nomeObjeto = item['ObjetoCusto.descricao'];
      const hasChilds = item['CentroCustoDrilldown.hasChilds'];
      const valor = item[CUSTO_POR_DIVISOR_BY_TOTAL];

      if (!mapa.has(id)) {
        mapa.set(id, {
          id,
          descricao: nomeCentro,
          hasChilds,
          idSuperior,
          objetos: {},
          children: []
        });
      }

      const grupo = mapa.get(id);
      grupo.objetos[nomeObjeto] = valor;
    });

    mapa.forEach(item => {
      if (item.idSuperior == null) {
        raizes.push(item);
      } else {
        const pai = mapa.get(item.idSuperior);
        if (pai) {
          pai.children.push(item);
        } else {
          raizes.push(item);
        }
      }
    });

    return raizes;
  };
}

function formatarValorFunction() {
  return valor => {
    if (!valor || valor === 0) return '0.00';
    return parseFloat(valor).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
}

function encontrarItemPorIdFunction(encontrarItemPorId) {
  return (items, id) => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children && item.children.length > 0) {
        const encontrado = encontrarItemPorId(item.children, id);
        if (encontrado) return encontrado;
      }
    }
    return null;
  };
}
